import { isLiveBotLevel } from '../bot/seats.ts';
import { coerceBotDials } from './dials.ts';
import { presetProfileKnobs } from './document.ts';
import { BOT_KNOB_LIMITS, clampBotKnob } from './limits.ts';
import { sanitizeBotName } from './name.ts';
import type {
  BotProfileDocument,
  BotProfileFlagKey,
  BotProfileKnobs,
  BotProfileNumberKey,
} from './types.ts';
import { CURRENT_BOT_PROFILE_VERSION, OLDEST_SUPPORTED_BOT_PROFILE_VERSION } from './version.ts';

const FLAG_KEYS: readonly BotProfileFlagKey[] = ['nudges', 'forecastHulls', 'followDistance'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Knobs from anything: each number is clamped to its range (whole where it must be), each flag
 * must be a boolean, and anything missing or malformed takes `fallback`'s value.
 */
export function coerceBotKnobs(value: unknown, fallback: BotProfileKnobs): BotProfileKnobs {
  const source = isRecord(value) ? value : {};
  const knobs: BotProfileKnobs = { ...fallback };

  for (const key of Object.keys(BOT_KNOB_LIMITS) as BotProfileNumberKey[]) {
    const raw = source[key];

    if (typeof raw === 'number' && Number.isFinite(raw)) {
      knobs[key] = clampBotKnob(key, raw);
    }
  }

  for (const key of FLAG_KEYS) {
    if (typeof source[key] === 'boolean') {
      knobs[key] = source[key];
    }
  }

  if (source.courseSense === 'time' || source.courseSense === 'distance') {
    knobs.courseSense = source.courseSense;
  }

  return knobs;
}

/**
 * Any stored profile, upgraded to the current version, or null when it is not a profile this
 * build can read. Same contract as `migrateBoatDocument`: missing knobs come from the preset the
 * profile is based on (Expert when that is missing too).
 */
export function migrateBotProfile(value: unknown): BotProfileDocument | null {
  if (!isRecord(value)) {
    return null;
  }

  const version = value.version;

  if (
    typeof version !== 'number' ||
    !Number.isFinite(version) ||
    version < OLDEST_SUPPORTED_BOT_PROFILE_VERSION ||
    version > CURRENT_BOT_PROFILE_VERSION
  ) {
    return null;
  }

  const basedOn = isLiveBotLevel(value.basedOn) ? value.basedOn : 'expert';

  return {
    version: CURRENT_BOT_PROFILE_VERSION,
    name: typeof value.name === 'string' ? sanitizeBotName(value.name) : '',
    basedOn,
    // v1 had no bars: its knobs were set one by one, so it opens as Custom.
    dials: version >= 2 ? coerceBotDials(value.dials) : null,
    knobs: coerceBotKnobs(value.knobs, presetProfileKnobs(basedOn)),
  };
}
